import type { DatasetCapabilities, DatasetColumn, ScenarioSuggestion } from 'src/components/star-grid/datasets/datasetTypes';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';

/** Only the features the mapped columns can serve: blends need a scenario column, FX and the default a measure. */
export function savedCapabilities(capabilities: DatasetCapabilities, columns: readonly DatasetColumn[]): DatasetCapabilities {
  const blends = capabilities.deck_blends;
  const scenarioMap = Object.fromEntries(Object.entries(blends?.scenario_map ?? {}).filter(([, value]) => value));
  const blended = blends && Object.keys(scenarioMap).length > 0 && columns.some((column) => column.role === 'scenario');
  const isMeasure = (name: string | undefined): boolean => columns.some((column) => column.role === 'measure' && column.name === name);
  const rates = capabilities.fx_rates;
  const ratesHold = rates && isMeasure(rates.local_measure) && columns.some((column) => column.name === rates.currency_column);
  return {
    ...(blended ? { deck_blends: { deck_types: [...blends.deck_types], scenario_map: scenarioMap } } : {}),
    ...(isMeasure(capabilities.fx_measure) ? { fx_measure: capabilities.fx_measure } : {}),
    ...(ratesHold ? { fx_rates: { ...rates } } : {}),
    ...(isMeasure(capabilities.measure) ? { measure: capabilities.measure } : {})
  };
}

export function withFxMeasure(capabilities: DatasetCapabilities, measure: string | undefined): DatasetCapabilities {
  const { fx_measure: _dropped, ...rest } = capabilities;
  return measure ? { ...rest, fx_measure: measure } : rest;
}

/** The suggested map, reporting scenario -> dataset value; scenarios with no suggestion stay unmapped. */
export function suggestedScenarioMap(suggestions: readonly ScenarioSuggestion[]): Record<string, string> {
  return Object.fromEntries(suggestions.map((suggestion) => [suggestion.reporting, suggestion.value]));
}

export function reportFeaturesSummary(capabilities: DatasetCapabilities, text: StarGridText): string {
  const parts = [
    ...(Object.keys(capabilities.deck_blends?.scenario_map ?? {}).length
      ? [`${text('wizardFeaturesScenarioMap')} (${Object.keys(capabilities.deck_blends?.scenario_map ?? {}).length})`]
      : []),
    ...(capabilities.fx_measure ? [`${text('wizardFeaturesFxMeasure')}: ${capabilities.fx_measure}`] : [])
  ];
  return parts.length ? parts.join('; ') : text('wizardFeaturesPlain');
}
